import React, { useState } from "react";
import { http, useFetch } from "@/lib/api";
import { PageHeader, StatCard, SectionTitle } from "@/components/common";
import { fmtUsd } from "@/lib/format";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { FlaskConical, Play, AlertTriangle, Loader2 } from "lucide-react";

const FIELDS = [
  ["max_age", "Max Age (min)", 30],
  ["max_mc", "Max Market Cap ($)", 250000],
  ["min_liquidity", "Min Liquidity ($)", 40000],
  ["min_momentum", "Min Momentum", 75],
  ["min_organic", "Min Organic", 65],
  ["max_risk", "Max Risk", 40],
];

export default function BacktestLab() {
  const [form, setForm] = useState(Object.fromEntries(FIELDS.map(([k, , d]) => [k, d])));
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const { data: past, reload } = useFetch("/backtests", []);

  const run = async () => {
    setRunning(true);
    try {
      const r = await http.post("/backtest/run", { name: `Strategy ${new Date().toLocaleTimeString()}`, ...form });
      setResult(r.data.data);
      reload();
    } finally {
      setRunning(false);
    }
  };

  const outcomeChart = result
    ? Object.entries(result.outcomes).map(([k, v]) => ({ milestone: `$${k}`, count: v }))
    : [];

  return (
    <div className="fade-in">
      <PageHeader icon={FlaskConical} title="Backtest Lab" subtitle="Simulate early-entry strategies against stored historical snapshots." />

      <div className="mb-4 flex items-center gap-2 rounded-lg border border-amber-700/40 bg-amber-950/20 px-4 py-2.5 text-xs font-semibold text-amber-300">
        <AlertTriangle className="h-4 w-4" /> HISTORICAL DATA — NOT A PREDICTION. Backtests describe the past; they do not claim a strategy will work in future.
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="pnut-card p-5">
          <SectionTitle>Strategy Criteria</SectionTitle>
          <div className="space-y-3">
            {FIELDS.map(([k, label]) => (
              <div key={k}>
                <label className="mb-1 block font-mono text-[10px] uppercase text-slate-500">{label}</label>
                <input
                  data-testid={`bt-${k}`}
                  type="number"
                  value={form[k]}
                  onChange={(e) => setForm({ ...form, [k]: Number(e.target.value) })}
                  className="w-full rounded-lg border border-slate-800 bg-void px-3 py-2 text-sm text-slate-200 outline-none focus:border-amber-600/50"
                />
              </div>
            ))}
            <button data-testid="bt-run" onClick={run} disabled={running} className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-amber-600 py-2.5 text-sm font-semibold text-amber-50 hover:bg-amber-500 disabled:opacity-60">
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Run Backtest
            </button>
          </div>
        </div>

        <div className="lg:col-span-2">
          {!result ? (
            <div className="pnut-card flex h-full min-h-64 items-center justify-center text-sm text-slate-500">
              Configure criteria and run a backtest to see historical outcomes.
            </div>
          ) : (
            <div className="space-y-4" data-testid="bt-results">
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <StatCard label="Sample Size" value={result.sample_size} sub={result.date_range} />
                <StatCard label="Failure Rate" value={`${result.failure_rate_pct}%`} tone="neg" />
                <StatCard label="Max Drawdown" value={`${result.max_drawdown_pct}%`} tone="neg" />
                <StatCard label="Median Outcome" value={fmtUsd(result.median_outcome_mc)} />
              </div>
              <div className="pnut-card p-4">
                <SectionTitle>Historical Outcomes (matching tokens that later reached…)</SectionTitle>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={outcomeChart}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis dataKey="milestone" tick={{ fill: "#64748b", fontSize: 11 }} />
                    <YAxis tick={{ fill: "#64748b", fontSize: 10 }} />
                    <Tooltip contentStyle={{ background: "#131822", border: "1px solid #334155", borderRadius: 8 }} />
                    <Bar dataKey="count" fill="#e6a15c" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                <p className="mt-2 text-[11px] text-slate-600">{result.disclaimer}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {past?.data?.length > 0 && (
        <div className="mt-6">
          <SectionTitle>Recent Backtests</SectionTitle>
          <div className="pnut-card overflow-hidden">
            <table className="pnut-table w-full">
              <thead><tr><th>Name</th><th>Sample</th><th>$250K</th><th>$1M</th><th>$5M</th><th>Failure Rate</th></tr></thead>
              <tbody>
                {past.data.map((b) => (
                  <tr key={b.id}>
                    <td className="text-slate-200">{b.name}</td>
                    <td className="font-mono text-slate-300">{b.sample_size}</td>
                    <td className="font-mono text-slate-300">{b.outcomes?.["250K"]}</td>
                    <td className="font-mono text-emerald-400">{b.outcomes?.["1M"]}</td>
                    <td className="font-mono text-emerald-400">{b.outcomes?.["5M"]}</td>
                    <td className="font-mono num-neg">{b.failure_rate_pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
